// Angular
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

// RxJS
import { Observable } from 'rxjs';
``

// Local
import { environment } from '../../../../environments/environment';
import { organizationsEndpoints } from '../../../types/api/endpoints/organizations.endpoints';
import { UtilitiesService } from '../../utilities/utilities-service.service';
import {
    MandatoryBillingReferenceTransactionType,
    NewUserRequest,
    NewUserResponse,
    NotificationOptions,
    OrganizationInformation,
    OrganizationInformationRequest,
    QueuedChange,
    QueuedChangesBatch,
    ListResponseOfState,
    UserAccesToSaveFilings,
    ListResponseOfUserAccesToSaveFilings,
    ListResponseOfUserDetails,
    ListResponseOfOrganizationItem,
    UserInfo,
    ListResponseOfUserItem,
    UserPermissions,
    UserRequest,
    UsersTransferRequest,
    ListResponseOfUserTypeItem,
} from '../../../types/api/dot-net-api-types/data-contracts';

/**
 * Interface for the Organizations API endpoints.
 */
export interface IOrganizationsEndpoints {
  getUserTypes(): Observable<ListResponseOfUserTypeItem>;
  getStates(): Observable<ListResponseOfState>;
  getTransactionTypes(): Observable<MandatoryBillingReferenceTransactionType[]>;
  getOrg(orgId: string): Observable<OrganizationInformation>;
  createOrg(orgId: string, params: OrganizationInformationRequest): Observable<object>;
  getJudgeQueues(orgId: string): Observable<ListResponseOfUserItem>;
  getUsers(orgId: string): Observable<ListResponseOfUserItem>;
  getManagementUsers(orgId: string): Observable<ListResponseOfUserDetails>;
  createUser(orgId: string, params: NewUserRequest): Observable<NewUserResponse | QueuedChange>;
  deleteUser(orgId: string, params: UserRequest): Observable<QueuedChangesBatch>;
  transferUser(orgId: string, params: UsersTransferRequest): Observable<QueuedChangesBatch>;
  getTransferOrganizations(orgId: string, name: string): Observable<ListResponseOfOrganizationItem>;
  getUser(orgId: string, userId: string): Observable<UserInfo>;
  updateUser(orgId: string, userId: string, params: UserInfo): Observable<object>;
  getUserPermissions(orgId: string, userId: string): Observable<UserPermissions>;
  updateUserPermissions(orgId: string, userId: string, params: UserPermissions): Observable<object>;
  resetUsersPasswords(orgId: string, params: UserRequest): Observable<object>;
  getTransactionsDraftsPermissions(orgId: string, userId: string): Observable<ListResponseOfUserAccesToSaveFilings>;
  updateTransactionsDraftsPermissions(orgId: string, userId: string, params: UserAccesToSaveFilings[]): Observable<void>;
  getNotificationsInbox(orgId: string, userId: string): Observable<NotificationOptions>;
  updateNotificationsInbox(orgId: string, userId: string, params: NotificationOptions): Observable<object>;
  getNotificationsMessageboard(orgId: string, userId: string): Observable<NotificationOptions>;
  updateNotificationsMessageboard(orgId: string, userId: string, params: NotificationOptions): Observable<object>;
}

/**
 * Base url for the api.
 */
const BASE_URL = environment.ApiServer.BaseURL;

/**
 * Organization endpoints for the api
 */
const ORGANIZATIONS_ENDPOINTS = organizationsEndpoints(BASE_URL);

/**
 * The service containing the Organizations API endpoints.
 */
@Injectable({ providedIn: 'root' })
export class OrganizationsApiService implements IOrganizationsEndpoints {

  /**
   * Constructor
   *
   * @param http The http client service.
   *
   * @param utilService A custom utility service used to build the url with params.
   */
  constructor(
    private readonly http: HttpClient,
    private readonly utilService: UtilitiesService
  ) { }

  /**
   * Endpoint to get user types
   */
  getUserTypes(): Observable<ListResponseOfUserTypeItem> {
    const url = ORGANIZATIONS_ENDPOINTS.getUserTypes();
    return <Observable<ListResponseOfUserTypeItem>>this.http.get(url);
  }

  /**
   * Endpoint to get states
   */
  getStates(): Observable<ListResponseOfState> {
    const url = ORGANIZATIONS_ENDPOINTS.getStates();
    return <Observable<ListResponseOfState>>this.http.get(url);
  }

  /**
   * Endpoint to get the transaction types an organization can require a billing reference for
   */
  getTransactionTypes(): Observable<MandatoryBillingReferenceTransactionType[]> {
    const url = ORGANIZATIONS_ENDPOINTS.getTransactionTypes();
    return this.http.get<MandatoryBillingReferenceTransactionType[]>(url);
  }

  /**
   * Endpoint to get organization
   * @param orgId
   */
  getOrg(orgId: string): Observable<OrganizationInformation> {
    const url = ORGANIZATIONS_ENDPOINTS.getOrg(orgId);
    return <Observable<OrganizationInformation>>this.http.get(url);
  }

  /**
   * Endpoint to create an organization
   * @param orgId
   * @param body
   */
  createOrg(orgId: string, body: OrganizationInformationRequest): (Observable<object>) {
    const url = ORGANIZATIONS_ENDPOINTS.createOrg(orgId);
    return <Observable<object>>this.http.put(url, body);
  }

  /**
   * Endpoint to get judge queues
   * @param orgId
   */
  getJudgeQueues(orgId: string): Observable<ListResponseOfUserItem> {
    const url = ORGANIZATIONS_ENDPOINTS.getJudgeQueues(orgId);
    return <Observable<ListResponseOfUserItem>>this.http.get(url);
  }

  /**
   * Endpoint to get users
   * @param orgId
   */
  getUsers(orgId: string): Observable<ListResponseOfUserItem> {
    const url = ORGANIZATIONS_ENDPOINTS.getUsers(orgId);
    return <Observable<ListResponseOfUserItem>>this.http.get(url);
  }

  /**
   * Endpoint to get management users
   * @param orgId
   */
  getManagementUsers(orgId: string): Observable<ListResponseOfUserDetails> {
    const url = ORGANIZATIONS_ENDPOINTS.getManagementUsers(orgId);
    return <Observable<ListResponseOfUserDetails>>this.http.get(url);
  }

  /**
   * Endpoint to create a user
   * @param orgId
   * @param body
   */
  createUser(orgId: string, body: NewUserRequest): Observable<NewUserResponse | QueuedChange> {
    const url = ORGANIZATIONS_ENDPOINTS.createUser(orgId);
    return <Observable<NewUserResponse | QueuedChange>>this.http.post(url, body);
  }

  /**
   * Endpoint to delete a user
   * @param orgId
   * @param userRequest
   */
  deleteUser(orgId: string, userRequest: UserRequest): Observable<QueuedChangesBatch> {
    const url = ORGANIZATIONS_ENDPOINTS.deleteUser(orgId);
    return <Observable<QueuedChangesBatch>>this.http.delete(url, {body: userRequest});
  }

  /**
   * Endpoint to transfer a user
   * @param orgId
   * @param params
   */
  transferUser(orgId: string, params: UsersTransferRequest): Observable<QueuedChangesBatch> {
    const url = ORGANIZATIONS_ENDPOINTS.transferUser(orgId);
    return <Observable<QueuedChangesBatch>>this.http.post(url, params);
  }

  /**
   * Endpoint to search the organizations a user can be transferred to
   * @param orgId
   * @param name
   */
  getTransferOrganizations(orgId: string, name: string): Observable<ListResponseOfOrganizationItem> {
    const url = this.utilService.buildUrlWithParams(ORGANIZATIONS_ENDPOINTS.getTransferOrganizations(orgId), { name });
    return <Observable<ListResponseOfOrganizationItem>>this.http.get(url);
  }

  /**
   * Endpoint to get a user's information.
   *
   * @param orgId The id of the organization the user belongs to.
   *
   * @param userId The id of the user to get.
   */
  getUser(orgId: string, userId: string): Observable<UserInfo> {
    const url = ORGANIZATIONS_ENDPOINTS.getUser(orgId, userId);
    return this.http.get<UserInfo>(url);
  }

  /**
   * Endpoint to update a user's information.
   *
   * @param orgId The id of the organization the user belongs to.
   *
   * @param userId The id of the user to update.
   *
   * @param body The UserInfo object containing the updates.
   */
  updateUser(orgId: string, userId: string, body: UserInfo): Observable<object> {
    // Guard clause to ensure barNumbers is always present.
    // - if user has no bar numbers then we must explicitly send an empty array.
    if (!body.barNumbers) {
      throw new Error('barNumbers array is required');
    }

    // Build the url with params.
    const url = ORGANIZATIONS_ENDPOINTS.updateUser(orgId, userId);

    // Return the PATCH request.
    return this.http.patch<object>(url, body);
  }

  /**
   * Endpoint to get user permissions
   * @param orgId
   * @param userId
   */
  getUserPermissions(orgId: string, userId: string): Observable<UserPermissions> {
    const url = ORGANIZATIONS_ENDPOINTS.getUserPermissions(orgId, userId);
    return <Observable<UserPermissions>>this.http.get(url);
  }

  /**
   * Endpoint to update user permissions
   * @param orgId
   * @param userId
   * @param body
   */
  updateUserPermissions(orgId: string, userId: string, body: UserPermissions): Observable<object> {
    const url = ORGANIZATIONS_ENDPOINTS.updateUserPermissions(orgId, userId);
    return <Observable<object>>this.http.patch(url, body);
  }

  /**
   * Endpoint to reset user passwords
   * @param orgId
   * @param userRequest
   */
  resetUsersPasswords(orgId: string, userRequest: UserRequest): Observable<object> {
    const url = ORGANIZATIONS_ENDPOINTS.resetUsersPasswords(orgId);
    return <Observable<object>>this.http.post(url, userRequest);
  }

  /**
   * Endpoint to get transactions drafts permissions
   * @param orgId
   * @param userId
   */
  getTransactionsDraftsPermissions(orgId: string, userId: string): Observable<ListResponseOfUserAccesToSaveFilings> {
    const url = ORGANIZATIONS_ENDPOINTS.getTransactionsDraftsPermissions(orgId, userId);
    return <Observable<ListResponseOfUserAccesToSaveFilings>>this.http.get(url);
  }

  /**
   * Endpoint to update transactions drafts permissions
   *
   * @param orgId
   * @param userId
   * @param params
   */
  updateTransactionsDraftsPermissions(orgId: string, userId: string, params: UserAccesToSaveFilings[]): Observable<void> {
    const url = ORGANIZATIONS_ENDPOINTS.updateTransactionsDraftsPermissions(orgId, userId);
    var dataObj = {data:params};
    return this.http.patch<void>(url, dataObj);
  }

  /**
   * Endpoint to get notifications inbox
   * @param orgId
   * @param userId
   */
  getNotificationsInbox(orgId: string, userId: string): Observable<NotificationOptions> {
    const url = ORGANIZATIONS_ENDPOINTS.getNotificationsInbox(orgId, userId);
    return <Observable<NotificationOptions>>this.http.get(url);
  }

  /**
   * Endpoint to update notifications inbox
   * @param orgId
   * @param userId
   * @param body
   */
  updateNotificationsInbox(orgId: string, userId: string, body: NotificationOptions): Observable<object> {
    const url = ORGANIZATIONS_ENDPOINTS.updateNotificationsInbox(orgId, userId);
    return <Observable<object>>this.http.patch(url, body);
  }

  /**
   * Endpoint to get notifications messageboard
   * @param orgId
   * @param userId
   */
  getNotificationsMessageboard(orgId: string, userId: string): Observable<NotificationOptions> {
    const url = ORGANIZATIONS_ENDPOINTS.getNotificationsMessageboard(orgId, userId);
    return <Observable<NotificationOptions>>this.http.get(url);
  }

  /**
   * Endpoint to update notifications messageboard
   * @param orgId
   * @param userId
   * @param body
   */
  updateNotificationsMessageboard(orgId: string, userId: string, body: NotificationOptions): Observable<object> {
    const url = ORGANIZATIONS_ENDPOINTS.updateNotificationsMessageboard(orgId, userId);
    return <Observable<object>>this.http.patch(url, body);
  }
}

